import React, { useState } from "react";
import { useChallenge } from "@/context/ChallengeContext";
import { PageHeader, DisclaimerFooter } from "@/components/Bits";
import SessionCard from "@/components/SessionCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fmtUSD, fmtDatePL, DAY_STATUS_META, STATUS_META } from "@/lib/calc";

const appliesResult = (s) => ["profit", "loss", "be"].includes(s.status);
const resultOf = (s) => (s.status === "be" ? 0 : Number(s.result_usd) || 0);

function SessionMini({ s }) {
  const m = STATUS_META[s.status];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: m.dot }} />
      <span className="text-xs">
        {s.status === "not_started" ? "—" : appliesResult(s) ? (resultOf(s) >= 0 ? "+" : "") + fmtUSD(resultOf(s)) : m.label}
      </span>
    </span>
  );
}

export default function ChallengeHistory() {
  const { data, saveReview } = useChallenge();
  const [openDay, setOpenDay] = useState(null);
  const [review, setReview] = useState({ good: "", bad: "", improve: "", followed_plan: "" });

  if (!data) return <div className="text-demon-muted font-mono py-20 text-center">Ładowanie...</div>;

  const perTurn = data.config.per_turn_1;

  const openDetail = (day) => {
    setOpenDay(day);
    setReview({
      good: day.review?.good || "",
      bad: day.review?.bad || "",
      improve: day.review?.improve || "",
      followed_plan: day.review?.followed_plan || "",
    });
  };

  const capBefore = (day) => {
    const arr = [];
    let run = day.opening_balance;
    day.sessions.forEach((s, i) => {
      arr[i] = run;
      if (appliesResult(s)) run += resultOf(s);
    });
    return arr;
  };

  const submitReview = async () => {
    await saveReview({ day: openDay.day, ...review });
  };

  return (
    <div>
      <PageHeader title="Challenge · Historia 92 dni" subtitle="01.10.2026 → 31.12.2026 · kliknij dzień, aby otworzyć szczegóły." />

      <div className="glow-card rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="history-table">
          <thead className="bg-violet-950/40 text-demon-muted text-[11px] tracking-widest sticky top-0">
            <tr>
              {["DZIEŃ", "DATA", "START", "TURA 1", "TURA 2", "TURA 3", "KONIEC DNIA", "PROFIT", "STATUS"].map((h) => (
                <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="font-mono">
            {data.days.map((d) => {
              const st = DAY_STATUS_META[d.status] || DAY_STATUS_META.not_started;
              const isToday = d.day === data.current_day;
              return (
                <tr
                  key={d.day}
                  data-testid={`history-row-${d.day}`}
                  onClick={() => openDetail(d)}
                  className={`border-t border-violet-900/20 cursor-pointer hover:bg-violet-600/10 transition ${
                    isToday ? "bg-violet-600/10" : ""
                  }`}
                >
                  <td className="px-4 py-2.5 text-white">{d.day}{isToday && <span className="text-violet-400"> ●</span>}</td>
                  <td className="px-4 py-2.5 text-demon-muted whitespace-nowrap">{fmtDatePL(d.date)}</td>
                  <td className="px-4 py-2.5">{fmtUSD(d.opening_balance)}</td>
                  <td className="px-4 py-2.5"><SessionMini s={d.sessions[0]} /></td>
                  <td className="px-4 py-2.5"><SessionMini s={d.sessions[1]} /></td>
                  <td className="px-4 py-2.5"><SessionMini s={d.sessions[2]} /></td>
                  <td className="px-4 py-2.5 text-white">{fmtUSD(d.closing_balance)}</td>
                  <td className={`px-4 py-2.5 ${d.profit > 0 ? "text-emerald-400" : d.profit < 0 ? "text-red-400" : "text-demon-muted"}`}>
                    {d.profit > 0 ? "+" : ""}{fmtUSD(d.profit)}
                  </td>
                  <td className={`px-4 py-2.5 text-xs font-semibold ${st.color}`}>{st.label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openDay} onOpenChange={(o) => !o && setOpenDay(null)}>
        <DialogContent className="bg-demon-surface border-violet-900/40 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          {openDay && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-glow">
                  Dzień {openDay.day} · {fmtDatePL(openDay.date)}
                </DialogTitle>
                <div className="text-sm text-demon-muted font-mono">
                  Start: {fmtUSD(openDay.opening_balance)} → Koniec: {fmtUSD(openDay.closing_balance)} ·{" "}
                  <span className={DAY_STATUS_META[openDay.status]?.color}>{DAY_STATUS_META[openDay.status]?.label}</span>
                </div>
              </DialogHeader>

              <div className="grid lg:grid-cols-3 gap-4 mt-2">
                {openDay.sessions.map((s, i) => (
                  <SessionCard
                    key={s.session}
                    day={openDay.day}
                    session={s.session}
                    sessionData={s}
                    capitalBefore={capBefore(openDay)[i]}
                    targetPct={perTurn}
                  />
                ))}
              </div>

              <div className="glow-card rounded-2xl p-5 mt-4">
                <h3 className="font-display font-semibold text-lg mb-3">Daily Review</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    ["good", "Co zrobiłem dobrze?"],
                    ["bad", "Co zrobiłem źle?"],
                    ["improve", "Co poprawię jutro?"],
                    ["followed_plan", "Czy trzymałem się planu?"],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-xs text-demon-muted">{label}</Label>
                      <Textarea
                        data-testid={`review-${k}`}
                        value={review[k]}
                        onChange={(e) => setReview((r) => ({ ...r, [k]: e.target.value }))}
                        className="mt-1 bg-black/50 border-violet-900/40 text-white min-h-[70px]"
                      />
                    </div>
                  ))}
                </div>
                <Button
                  data-testid="save-review-button"
                  onClick={submitReview}
                  className="mt-4 font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0"
                >
                  ZAPISZ PODSUMOWANIE
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DisclaimerFooter />
    </div>
  );
}
